import {
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import NewSchoolDialog from './NewSchoolDialog';
import NewDistrictDialog from './NewDistrictDialog';
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PresentationChromeContext } from '../../store/presentation-chrome-context';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonIcon from '@mui/icons-material/Person';
import { MangedForm } from '../../ui/Form/MangedForm';
import { downloadReport, getOlympicsEditions, setWorkingOlympicsEdition } from '../../api/content-api';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import { loginPage } from '../../store/fixed-routing';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import OlympicsContext from '../../store/olympics-context';
import { debugError, debugLog } from '../../utils/debug';
import NewStudentDialog from './NewStudentDialog';
import { STAGE_0, STAGES_TITLES } from '../../utils/stages';
import ChangePublishedStageDialog from './ChangePublishedStageDialog';
import { StageOption } from './StagesTable';
import TeacherEditDialog from './TeacherEditDialog';
import Olympics from '../../model/Olympics';
import { romanize } from '../../utils/string-tools';

type OlympicsEditionOption = {
	id: number;
	edition: number;
	school_year?: string;
	is_latest?: boolean;
	is_working?: boolean;
};

type Props = {
	index: number;
	onRefresh?: () => void;
};

const PanelHeader = ({ index, onRefresh }: Props) => {
	const theme = useTheme();
	const { palette } = theme;
	const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
	const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
	const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
	const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
	const isStages = index === 5;
	const isBelow640 = useMediaQuery('(max-width:640px)');
	const isBelow1535 = useMediaQuery('(max-width:1535px)');
	const isBelow1435 = useMediaQuery('(max-width:1435px)');
	const isBetween600_640 = useMediaQuery('(min-width:600px) and (max-width:640px)');
	const isBetween900_910 = useMediaQuery('(min-width:900px) and (max-width:910px)');
	const isBetween600_1200 = useMediaQuery('(min-width:600px) and (max-width:1200px)');
	const isRefreshIcon = isBelow640 || (isStages && isBelow1535);
	const controlWidth = {
		xs: '100%',
		sm: '180px',
		md: '200px',
		lg: '230px',
	};
	const buttonWidth = {
		xs: '44px',
		sm: '180px',
		md: '200px',
		lg: '230px',
	};
	const iconButtonWidth = {
		xs: '36px',
		sm: '36px',
		md: '44px',
	};
	const searchControlWidth = (isStages && isBetween600_640) || (!isStages && isBetween900_910) ? '50%' : '100%';
	const compactIconSx = isCompact
		? {
				'& .MuiButton-startIcon': { m: 0 },
				'& .MuiButton-endIcon': { m: 0 },
			}
		: {};
	const [openNewSchoolDialog, setOpenNewSchoolDialog] = useState(false);
	const [openNewDistrictDialog, setOpenNewDistrictDialog] = useState(false);
	const [openNewStudentDialog, setOpenNewStudentDialog] = useState(false);
	const [openTeacherEditDialog, setOpenTeacherEditDialog] = useState(false);
	const { token, logout, isSecretary, isTeacher, isAdmin, districtId, id, role } = useContext(AuthContext);
	const {
		olympics,
		setOlympics,
		setDistrictFilterId,
		districtFilterId,
		districts,
		searchFilterValue,
		setSearchFilterValue,
		isRefreshing,
		refresh,
		refreshTables,
	} = useContext(OlympicsContext);
	const navigate = useNavigate();
	const location = useLocation();
	const [, setSearchParams] = useSearchParams();
	const { setMsg } = useContext(SnackbarContext);
	const [isSending, setIsSending] = useState(false);
	const [publishedStageForDialog, setPublishedStageForDialog] = useState<StageOption>(STAGE_0);
	const [enabledDistrictSearch, setEnabledDistrictSearch] = useState(false);
	const [versions, setVersions] = useState({
		fe: 'unknown',
		be: 'unknown',
		th: 'unknown',
		variant: 'unknown',
	});
	const [editions, setEditions] = useState<OlympicsEditionOption[]>([]);
	const [isSwitchingEdition, setIsSwitchingEdition] = useState(false);
	const { hideDevChrome } = useContext(PresentationChromeContext);
	const headerRef = useRef<HTMLDivElement | null>(null);
	const FILTER_QUERY_DISTRICT = 'o';
	const FILTER_QUERY_SEARCH = 'q';
	const filtersInitializedRef = useRef(false);

	useEffect(() => {
		const enabled = !isSecretary && index !== 1 && index !== 6 && index !== 8 && index !== 9;
		if (!enabled) {
			setDistrictFilterId(districtId ? districtId : -1);
		}
		setEnabledDistrictSearch(enabled);
	}, [index, districtId, isAdmin, isSecretary, setDistrictFilterId]);

	useLayoutEffect(() => {
		if (index === 1 || index === 6 || index === 8 || index === 9 || index === 10) {
			return;
		}
		filtersInitializedRef.current = false;
		const safeDistrictId = districtId ? districtId : -1;
		const districtSessionKey = `olympics_panel_district_${index}`;
		const searchSessionKey = `olympics_panel_search_${index}`;
		const parseDistrict = (value: string | null): number | null => {
			if (!value) {
				return null;
			}
			const parsed = Number(value);
			return Number.isNaN(parsed) ? null : parsed;
		};

		const currentParams = new URLSearchParams(location.search);
		const districtFromUrl = parseDistrict(currentParams.get(FILTER_QUERY_DISTRICT));
		const districtFromSession = parseDistrict(sessionStorage.getItem(districtSessionKey));
		const fallbackDistrict = enabledDistrictSearch ? -1 : safeDistrictId;
		let nextDistrict = districtFromUrl ?? districtFromSession ?? fallbackDistrict;
		if (!enabledDistrictSearch || isSecretary) {
			nextDistrict = safeDistrictId;
		}
		let pendingSync = false;
		if (districtFilterId !== nextDistrict) {
			setDistrictFilterId(nextDistrict);
			pendingSync = true;
		}

		const searchFromUrl = currentParams.get(FILTER_QUERY_SEARCH) ?? '';
		const searchFromSession = sessionStorage.getItem(searchSessionKey) ?? '';
		const nextSearch = searchFromUrl || searchFromSession || '';
		if ((searchFilterValue || '') !== nextSearch) {
			setSearchFilterValue(nextSearch);
			pendingSync = true;
		}
		debugLog('PanelHeader URL->state sync', {
			index,
			districtFromUrl,
			nextDistrict,
			searchFromUrl,
			nextSearch,
			pendingSync,
		});
		if (pendingSync) {
			return;
		}
		filtersInitializedRef.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [index, enabledDistrictSearch, isSecretary, districtId, location.search]);

	useEffect(() => {
		if (index === 1 || index === 6) {
			return;
		}
		if (!filtersInitializedRef.current) {
			return;
		}
		const districtSessionKey = `olympics_panel_district_${index}`;
		const searchSessionKey = `olympics_panel_search_${index}`;
		const safeDistrictId = districtId ? districtId : -1;
		const safeDistrict = !enabledDistrictSearch || isSecretary ? safeDistrictId : districtFilterId;
		const safeSearch = searchFilterValue || '';

		sessionStorage.setItem(districtSessionKey, String(safeDistrict));
		sessionStorage.setItem(searchSessionKey, safeSearch);

		const currentParams = new URLSearchParams(location.search);
		const currentDistrict = currentParams.get(FILTER_QUERY_DISTRICT) ?? '';
		const currentSearch = currentParams.get(FILTER_QUERY_SEARCH) ?? '';
		const targetDistrict = String(safeDistrict);
		const targetSearch = safeSearch.trim() ? safeSearch : '';
		const districtChanged = currentDistrict !== targetDistrict;
		const searchChanged = currentSearch !== targetSearch;
		if (districtChanged || searchChanged) {
			const nextParams = new URLSearchParams(location.search);
			nextParams.set(FILTER_QUERY_DISTRICT, targetDistrict);
			if (targetSearch) {
				nextParams.set(FILTER_QUERY_SEARCH, targetSearch);
			} else {
				nextParams.delete(FILTER_QUERY_SEARCH);
			}
			debugLog('PanelHeader state->URL sync', {
				index,
				targetDistrict,
				targetSearch,
				before: currentParams.toString(),
				after: nextParams.toString(),
			});
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		index,
		districtId,
		districtFilterId,
		searchFilterValue,
		enabledDistrictSearch,
		isSecretary,
		location.search,
		setSearchParams,
	]);

	const fetchVersions = () => {
		// Read versions from meta tags
		const feMeta = document.querySelector('meta[name="app-version"]')?.getAttribute('content') || 'FE:unknown';
		const thMeta = document.querySelector('meta[name="theme-version"]')?.getAttribute('content') || 'TH:unknown';

		// Fetch BE version from API (always)
		fetch('/wp-json/olympics/version')
			.then((res) => res.json())
			.then((data) => {
				setVersions({
					fe: feMeta,
					be: data.version || 'BE:unknown',
					th: thMeta,
					variant: data.variant || 'unknown',
				});
			})
			.catch(() => {
				setVersions({ fe: feMeta, be: 'BE:unknown', th: thMeta, variant: 'unknown' });
			});
	};

	useEffect(() => {
		// Fetch versions on mount and when index changes (navigating between options)
		fetchVersions();

		// Set up periodic refresh every 30 seconds (version is quick, doesn't hit DB)
		const intervalId = setInterval(() => {
			fetchVersions();
		}, 30000); // 30 seconds

		return () => clearInterval(intervalId);
	}, [index]);

	useEffect(() => {
		if (!token || (!isAdmin && !isSecretary)) {
			setEditions([]);
			return;
		}
		getOlympicsEditions(token)
			.then((response) => {
				if (response.status === 200 && Array.isArray(response.data?.editions)) {
					setEditions(response.data.editions);
				}
			})
			.catch((exception) => {
				debugError('getOlympicsEditions', exception);
			});
	}, [token, isAdmin, isSecretary, olympics?.id]);

	const handleWorkingEditionChange = (event: SelectChangeEvent<number>) => {
		const nextId = Number(event.target.value);
		if (!token || !nextId || nextId === olympics?.id) {
			return;
		}
		setIsSwitchingEdition(true);
		setWorkingOlympicsEdition(token, nextId)
			.then((response) => {
				if (response.status === 200) {
					setOlympics(Olympics.fromApiResponse(response.data));
					// Filtr okręgu trzyma id z poprzedniej edycji — bez resetu listy są puste.
					if (!isSecretary) {
						setDistrictFilterId(-1);
					}
					refresh();
					refreshTables();
					const label = romanize(response.data?.edition ?? nextId);
					setMsg({
						msg: `Przełączono kontekst panelu na edycję ${label}.`,
						severity: 'success',
					});
				} else {
					setMsg({ msg: response.data?.message || 'Nie udało się przełączyć edycji' });
				}
			})
			.catch((exception) => {
				debugError(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: exception?.response?.data?.message || 'Błąd serwera. Spróbuj za chwilę' });
				}
			})
			.finally(() => setIsSwitchingEdition(false));
	};

	useEffect(() => {
		if (!headerRef.current) {
			return;
		}
		const element = headerRef.current;
		const updateHeight = () => {
			const height = Math.ceil(element.getBoundingClientRect().height);
			document.documentElement.style.setProperty('--panel-header-height', `${height}px`);
		};
		updateHeight();
		const observer = new ResizeObserver(updateHeight);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	const handlePublishedStageForDialog = (event: SelectChangeEvent<StageOption>) => {
		const value = event.target.value as unknown as StageOption;
		if (olympics && value.key > olympics.getPublishedStage().key) {
			setPublishedStageForDialog(value);
		}
	};

	const getTitle = () => {
		switch (index) {
			case 1:
				return 'Okręgi';
			case 2:
				return 'Szkoły';
			case 3:
				return 'Nauczyciele';
			case 4:
				return 'Uczniowie';
			case 5:
				return 'Etapy';
			case 6:
				return 'Ustawienia';
			case 7:
				return 'Wysyłki';
			case 8:
				return 'Zarządzanie';
			case 9:
				return 'Co nowego';
			case 10:
				return 'Pomoc';
		}
	};

	const getReportType = () => {
		switch (index) {
			case 2:
				return 'schools';
			case 3:
				return 'teachers';
			case 4:
				return 'students';
			case 5:
				return 'results';
			case 7:
				return 'campaigns';
			default:
				return 'districts';
		}
	};

	const downloadReportByType = (type: string) => {
		setIsSending(true);
		downloadReport(token, type)
			.then((response) => {
				const now = new Date();
				const year = now.getFullYear().toString().slice(-2);
				const month = String(now.getMonth() + 1).padStart(2, '0');
				const day = String(now.getDate()).padStart(2, '0');
				const hours = String(now.getHours()).padStart(2, '0');
				const minutes = String(now.getMinutes()).padStart(2, '0');
				const seconds = String(now.getSeconds()).padStart(2, '0');
				const timestamp = `${year}-${month}-${day}-${hours}-${minutes}-${seconds}`;
				let fileName: string;
				const fileNameSuffix =
					'-' + process.env.REACT_APP_NAME + '-' + (olympics?.edition || '') + '-' + timestamp + '.xlsx';
				switch (type) {
					case 'schools': {
						fileName = 'Szkoły' + fileNameSuffix;
						break;
					}
					case 'teachers': {
						fileName = 'Nauczyciele' + fileNameSuffix;
						break;
					}
					case 'students': {
						fileName = 'Uczniowie' + fileNameSuffix;
						break;
					}
					case 'results': {
						fileName = 'Wyniki' + fileNameSuffix;
						break;
					}
					case 'stage3-codes': {
						fileName = 'Etykiety-kodow-etap-III' + fileNameSuffix;
						break;
					}
					case 'campaigns': {
						fileName = 'Kampanie' + fileNameSuffix;
						break;
					}
					default: {
						fileName = 'Okręgi' + fileNameSuffix;
						break;
					}
				}
				const url = window.URL.createObjectURL(response.data);
				const a = document.createElement('a');
				a.style.display = 'none';
				a.setAttribute('download', fileName);
				a.href = url;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				window.URL.revokeObjectURL(url);
				setIsSending(false);
				setMsg({ msg: 'Pobrano nowy raport', severity: 'info' });
			})
			.catch((exception) => {
				setIsSending(false);
				debugError('PanelHeader error:', exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					const msg = exception?.response?.data?.message
						? exception?.response?.data?.message
						: 'Błąd serwera. Spróbuj za chwilę';
					setMsg({ msg });
				}
			});
	};

	const downloadExcel = () => {
		downloadReportByType(getReportType());
	};
	const clearSearchEverywhere = () => {
		setSearchFilterValue('');
		if (index !== 1 && index !== 6 && index !== 8 && index !== 9) {
			// Remove search memory globally for all panel views.
			const allSessionKeys = Object.keys(sessionStorage);
			allSessionKeys.forEach((key) => {
				if (key.startsWith('olympics_panel_search_')) {
					sessionStorage.removeItem(key);
				}
			});
			const allLocalKeys = Object.keys(localStorage);
			allLocalKeys.forEach((key) => {
				if (key.startsWith('olympics_panel_search_')) {
					localStorage.removeItem(key);
				}
			});
			const nextParams = new URLSearchParams(location.search);
			nextParams.delete(FILTER_QUERY_SEARCH);
			setSearchParams(nextParams, { replace: true });
		}
	};

	return (
		<Box ref={headerRef}>
			{/* Version watermark — non-production hosts only (not olijp.pl / olijpsp.pl) */}
			{(() => {
				const hostname = window.location.hostname;
				const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
				const isTest = hostname.includes('test-') || hostname.includes('-test.');
				const isPreprod = hostname.includes('prod-') || hostname.includes('preprod') || hostname.includes('-pre.');
				// Coolify / OF demo (e.g. filodemo.janko-soft.com) — not matched by test-* alone
				const isFiloDemo =
					hostname.includes('filodemo') || hostname.includes('demo.janko-soft') || hostname.startsWith('demo.');
				return (isLocal || isTest || isPreprod || isFiloDemo) && !hideDevChrome;
			})() && (
				<Typography
					sx={{
						position: 'fixed',
						top: 10,
						left: 10,
						fontSize: '11px',
						fontWeight: 'bold',
						color: 'rgba(0,0,0,0.90)',
						zIndex: 9999,
						lineHeight: 1.4,
						userSelect: 'none',
						pointerEvents: 'none',
						textShadow: '1px 1px 2px rgba(255,255,255,0.8), 0 0 4px rgba(255,255,255,0.5)',
						backgroundColor: 'rgba(255,255,255,0.90)',
						padding: '4px 8px',
						borderRadius: '4px',
						backdropFilter: 'blur(2px)',
						letterSpacing: '0.3px',
					}}
				>
					{versions.be}
					<br />
					{versions.fe}
					<br />
					{versions.th}
					<br />
					{versions.variant}
				</Typography>
			)}

			{(isAdmin || isSecretary) && editions.length > 1 ? (
				<FormControl
					size='small'
					sx={{ minWidth: 180, maxWidth: 280 }}
				>
					<InputLabel id='working-edition-label'>Edycja w panelu</InputLabel>
					<Select
						labelId='working-edition-label'
						label='Edycja w panelu'
						value={olympics?.id || ''}
						disabled={isSwitchingEdition}
						onChange={handleWorkingEditionChange}
					>
						{editions.map((ed) => (
							<MenuItem
								key={ed.id}
								value={ed.id}
							>
								{romanize(ed.edition)}
								{ed.school_year ? ` · ${ed.school_year}` : ''}
								{ed.is_latest ? ' (bieżąca)' : ''}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			) : (
				<Typography
					variant={'h5'}
					sx={{
						color: palette.primary.main,
					}}
				>
					Edycja {olympics?.edition}
				</Typography>
			)}
			{isSwitchingEdition && <CircularProgress size={20} />}
			<Typography
				variant={'h3'}
				sx={{
					color: palette.primary.main,
					mb: '10px',
				}}
			>
				{getTitle()}
			</Typography>
			<Divider />
			<Grid
				item
				container
				justifyContent={'space-between'}
				gap={{ xs: 1, sm: 2, lg: 0 }}
				sx={{
					mt: { xs: 2, sm: 3 },
					flexWrap: 'wrap',
					alignItems: 'center',
				}}
			>
				<Grid
					item
					container
					xs={12}
					order={{ xs: 2, lg: 1 }}
					gap={{ xs: 1, sm: 2 }}
					sx={{
						flexWrap: 'wrap',
						alignItems: 'center',
						flexGrow: { lg: 0 },
						flexShrink: { lg: 0 },
						flexBasis: { xs: '100%', lg: 'auto' },
						maxWidth: { xs: '100%', lg: 'fit-content' },
					}}
				>
					{isLgUp && index !== 6 && index !== 8 && index !== 9 && (isSecretary || isAdmin) && (
						<Button
							sx={{
								width: buttonWidth,
								minWidth: buttonWidth,
								px: isCompact ? 0.5 : 2,
								...compactIconSx,
							}}
							size={isCompact ? 'small' : 'medium'}
							disabled={isSending}
							endIcon={isSending ? <CircularProgress size={20} /> : <FileDownloadIcon />}
							variant={'outlined'}
							onClick={() => downloadExcel()}
							aria-label='Pobierz raport'
							title='Pobierz raport'
						>
							{'Pobierz raport'}
						</Button>
					)}
					{index === 2 && isAdmin && !isBetween600_1200 && (
						<Tooltip
							title='Nowa szkoła'
							disableHoverListener={!isBelow1435}
							disableFocusListener={!isBelow1435}
							disableTouchListener={!isBelow1435}
						>
							<Button
								endIcon={<AddIcon />}
								variant={'outlined'}
								onClick={() => setOpenNewSchoolDialog(true)}
								sx={{
									width: isBelow1435 ? iconButtonWidth : buttonWidth,
									minWidth: isBelow1435 ? iconButtonWidth : buttonWidth,
									px: isCompact ? 0.5 : 2,
									...(isBelow1435 ? compactIconSx : {}),
								}}
								size={isCompact ? 'small' : 'medium'}
								aria-label='Nowa szkoła'
								title='Nowa szkoła'
							>
								{isBelow1435 ? null : 'Nowa szkoła'}
							</Button>
						</Tooltip>
					)}
					{index === 1 && isAdmin && (
						<Tooltip
							title='Nowy okręg'
							disableHoverListener={!isBelow1435}
							disableFocusListener={!isBelow1435}
							disableTouchListener={!isBelow1435}
						>
							<Button
								endIcon={<AddIcon />}
								variant={'outlined'}
								onClick={() => setOpenNewDistrictDialog(true)}
								sx={{
									width: isBelow1435 ? iconButtonWidth : buttonWidth,
									minWidth: isBelow1435 ? iconButtonWidth : buttonWidth,
									px: isCompact ? 0.5 : 2,
									...(isBelow1435 ? compactIconSx : {}),
								}}
								size={isCompact ? 'small' : 'medium'}
								aria-label='Nowy okręg'
								title='Nowy okręg'
							>
								{isBelow1435 ? null : 'Nowy okręg'}
							</Button>
						</Tooltip>
					)}
					{index === 4 && isAdmin && !isBetween600_1200 && (
						<Tooltip
							title='Nowy uczeń'
							disableHoverListener={!isBelow1435}
							disableFocusListener={!isBelow1435}
							disableTouchListener={!isBelow1435}
						>
							<Button
								endIcon={<AddIcon />}
								variant={'outlined'}
								onClick={() => setOpenNewStudentDialog(true)}
								sx={{
									width: isBelow1435 ? iconButtonWidth : buttonWidth,
									minWidth: isBelow1435 ? iconButtonWidth : buttonWidth,
									px: isCompact ? 0.5 : 2,
									...(isBelow1435 ? compactIconSx : {}),
								}}
								size={isCompact ? 'small' : 'medium'}
								aria-label='Nowy uczeń'
								title='Nowy uczeń'
							>
								{isBelow1435 ? null : 'Nowy uczeń'}
							</Button>
						</Tooltip>
					)}
					{isTeacher && (
						<Tooltip
							title='Dane konta'
							disableHoverListener={!isCompact}
							disableFocusListener={!isCompact}
							disableTouchListener={!isCompact}
						>
							<Button
								endIcon={<PersonIcon />}
								variant={'outlined'}
								onClick={() => setOpenTeacherEditDialog(true)}
								sx={{
									width: buttonWidth,
									minWidth: buttonWidth,
									px: isCompact ? 0.5 : 2,
									...compactIconSx,
								}}
								size={isCompact ? 'small' : 'medium'}
								aria-label='Dane konta'
								title='Dane konta'
							>
								{isCompact ? null : 'Dane konta'}
							</Button>
						</Tooltip>
					)}
				</Grid>
				<Grid
					item
					container
					justifyContent={{ xs: 'space-between', lg: 'end' }}
					xs={12}
					order={{ xs: 1, lg: 2 }}
					gap={{ xs: 1, sm: 2 }}
					sx={{
						flexWrap: 'wrap',
						alignItems: 'center',
						flexGrow: { lg: 1 },
						flexBasis: { xs: '100%', lg: 0 },
						minWidth: 0,
					}}
				>
					{index !== 1 && index !== 6 && index !== 8 && index !== 9 && (
						<>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									width: controlWidth,
								}}
							>
								<FormControl
									sx={{
										width: searchControlWidth,
										flexGrow: searchControlWidth === '50%' ? 0 : 1,
										minWidth: 0,
										borderColor: palette.primary.main,
										color: palette.primary.main,
										'& fieldset': {
											borderColor: palette.primary.main + '!important',
										},
										'& svg': {
											color: palette.primary.main,
										},
									}}
								>
									<TextField
										sx={{
											color: palette.primary.main,
											borderColor: palette.primary.main,
										}}
										fullWidth
										size={'small'}
										label='Szukaj'
										value={searchFilterValue || ''}
										onChange={(event) => setSearchFilterValue(event.target.value)}
										InputProps={{
											endAdornment: searchFilterValue ? (
												<InputAdornment position='end'>
													<IconButton
														size='small'
														aria-label='Wyczyść wyszukiwanie'
														onClick={clearSearchEverywhere}
													>
														<CloseIcon fontSize='small' />
													</IconButton>
												</InputAdornment>
											) : undefined,
										}}
									></TextField>
								</FormControl>
								{isRefreshIcon && index !== 1 && index !== 6 && index !== 9 && onRefresh && (
									<Tooltip title='Odśwież'>
										<Button
											variant='outlined'
											onClick={onRefresh}
											disabled={isRefreshing}
											sx={{
												width: iconButtonWidth,
												minWidth: 0,
												px: 0.5,
												borderColor: palette.primary.main,
												color: palette.primary.main,
												...compactIconSx,
											}}
											size='small'
											aria-label='Odśwież'
										>
											{isRefreshing ? (
												<CircularProgress
													size={16}
													color='inherit'
												/>
											) : (
												<RefreshIcon />
											)}
										</Button>
									</Tooltip>
								)}
							</Box>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									width: {
										xs: '100%',
										sm: '216px',
										md: '244px',
										lg: '230px',
									},
								}}
							>
								<FormControl
									sx={{
										flexGrow: 1,
										minWidth: 0,
										borderColor: palette.primary.main,
										color: palette.primary.main,
										'& fieldset': {
											borderColor: palette.primary.main + '!important',
										},
										'& svg': {
											color: palette.primary.main,
										},
									}}
								>
									<InputLabel
										sx={{
											color: palette.primary.main,
											borderColor: palette.primary.main,
										}}
									>
										Wybierz okręg
									</InputLabel>
									<Select
										sx={{
											color: palette.primary.main,
											borderColor: palette.primary.main,
										}}
										fullWidth
										disabled={!enabledDistrictSearch}
										size={'small'}
										value={enabledDistrictSearch ? districtFilterId : districtId ? districtId : -1}
										label='Wybierz okręg'
										onChange={(event) => setDistrictFilterId(Number(event.target.value))}
									>
										<MenuItem value={-1}>Wszystkie</MenuItem>
										{districts.map((d) => (
											<MenuItem
												value={d.id}
												key={d.name}
											>
												{d.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								{isLgDown && index !== 6 && index !== 8 && index !== 9 && (isSecretary || isAdmin) && (
									<Tooltip title='Pobierz raport'>
										<Button
											variant='outlined'
											onClick={() => downloadExcel()}
											disabled={isSending}
											sx={{
												width: iconButtonWidth,
												minWidth: 0,
												px: 0.5,
												borderColor: palette.primary.main,
												color: palette.primary.main,
												...compactIconSx,
											}}
											size='small'
											aria-label='Pobierz raport'
										>
											{isSending ? <CircularProgress size={16} /> : <FileDownloadIcon />}
										</Button>
									</Tooltip>
								)}
								{isBetween600_1200 && index === 2 && isAdmin && (
									<Tooltip title='Nowa szkoła'>
										<Button
											variant='outlined'
											onClick={() => setOpenNewSchoolDialog(true)}
											sx={{
												width: iconButtonWidth,
												minWidth: iconButtonWidth,
												px: 0.5,
												borderColor: palette.primary.main,
												color: palette.primary.main,
												...compactIconSx,
											}}
											size='small'
											aria-label='Nowa szkoła'
										>
											<AddIcon />
										</Button>
									</Tooltip>
								)}
								{isBetween600_1200 && index === 4 && isAdmin && (
									<Tooltip title='Nowy uczeń'>
										<Button
											variant='outlined'
											onClick={() => setOpenNewStudentDialog(true)}
											sx={{
												width: iconButtonWidth,
												minWidth: iconButtonWidth,
												px: 0.5,
												borderColor: palette.primary.main,
												color: palette.primary.main,
												...compactIconSx,
											}}
											size='small'
											aria-label='Nowy uczeń'
										>
											<AddIcon />
										</Button>
									</Tooltip>
								)}
							</Box>
						</>
					)}
					{(isSecretary || isAdmin) && index === 5 && (
						<>
							<FormControl
								sx={{
									width: controlWidth,
									minWidth: 0,
									borderColor: palette.primary.main,
									color: palette.primary.main,
									'& fieldset': {
										borderColor: palette.primary.main + '!important',
									},
									'& svg': {
										color: palette.primary.main,
									},
								}}
							>
								<InputLabel
									sx={{
										ml: { xs: 0, lg: '20px' },
										color: palette.primary.main,
										borderColor: palette.primary.main,
									}}
								>
									Opublikowane etapy
								</InputLabel>
								<Select
									sx={{
										color: palette.primary.main,
										borderColor: palette.primary.main,
									}}
									fullWidth
									disabled={!isAdmin}
									size={'small'}
									value={olympics ? olympics.getPublishedStage() : STAGE_0}
									label={'Opublikwane etapy'}
									onChange={(event) => handlePublishedStageForDialog(event)}
								>
									{STAGES_TITLES.map((s: any) => (
										<MenuItem
											key={s.title}
											value={s}
											sx={{ fontSize: '16px' }}
											disabled={!!olympics && olympics.getPublishedStage().key >= s.key}
										>
											{s.title}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<ChangePublishedStageDialog
								stage={publishedStageForDialog}
								close={() => setPublishedStageForDialog(STAGE_0)}
								onSuccess={() => {
									setPublishedStageForDialog(STAGE_0);
								}}
							/>
						</>
					)}
					{/* Refresh Button */}
					{!isRefreshIcon && index !== 1 && index !== 6 && index !== 9 && onRefresh && (
						<Button
							startIcon={
								isRefreshing ? (
									<CircularProgress
										size={16}
										color='inherit'
									/>
								) : (
									<RefreshIcon />
								)
							}
							variant='outlined'
							onClick={onRefresh}
							disabled={isRefreshing}
							sx={{
								width: buttonWidth,
								minWidth: buttonWidth,
								px: isCompact ? 0.5 : 2,
								borderColor: palette.primary.main,
								color: palette.primary.main,
								...compactIconSx,
							}}
							size='medium'
						>
							Odśwież
						</Button>
					)}
				</Grid>
			</Grid>
			<MangedForm>
				<NewSchoolDialog
					open={openNewSchoolDialog}
					onClose={() => setOpenNewSchoolDialog(false)}
				/>
			</MangedForm>
			<MangedForm>
				<NewDistrictDialog
					open={openNewDistrictDialog}
					onClose={() => setOpenNewDistrictDialog(false)}
				/>
			</MangedForm>
			<MangedForm>
				<NewStudentDialog
					open={openNewStudentDialog}
					onClose={() => setOpenNewStudentDialog(false)}
				/>
			</MangedForm>
			<MangedForm>
				{openTeacherEditDialog && (
					<TeacherEditDialog
						teacherWpId={id}
						open={openTeacherEditDialog}
						onClose={() => setOpenTeacherEditDialog(false)}
						onSuccess={() => {
							setOpenTeacherEditDialog(false);
							refreshTables(); // Silent refresh after edit
						}}
					/>
				)}
			</MangedForm>
		</Box>
	);
};

export default PanelHeader;
